import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button, Card, Col, Divider, Row, Typography } from "antd";
import { toast } from "utils/toast";

import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
} from "js/components/Loading";
import { Details } from "js/components/Memberships/types";
import { Button as RetryButton } from "js/components/ui/button";

import { activateMembership, cancelMembership } from "../../../api/memberships";
import { membershipDataUpdated } from "../../../store/features/memberhsip/membership";
import { loadMembershipData } from "../../../store/features/memberhsip/membershipActions";

const { Title, Text } = Typography;

const MembershipConfiguration: React.FC = () => {
  const [details, setDetails] = useState<Details>({
    id: "",
    is_auto_renewal: false,
    plan: "",
    price: 0,
    renewal_date: "",
    user_members: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isAutoRenewal, setIsAutoRenewal] = useState(false);

  const { membershipData, loading: membershipLoading } = useSelector(
    (state) => state.entities.membership,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleAutoRenewalAction = async () => {
    setLoading(true);

    if (isAutoRenewal) {
      await cancelMembership(details.id)
        .then(() => {
          dispatch(
            membershipDataUpdated({
              ...membershipData,
              is_auto_renewal: false,
            }),
          );
          setIsAutoRenewal(false);
          toast.success("Auto-renovación cancelada");
        })
        .catch(({ response }) => {
          console.error("Error: ", response);
          toast.error(`Error: ${response?.data?.error || "Error desconocido"}`);
        })
        .finally(() => setLoading(false));
    } else {
      await activateMembership(details.id)
        .then(() => {
          dispatch(
            membershipDataUpdated({
              ...membershipData,
              is_auto_renewal: true,
            }),
          );
          setIsAutoRenewal(true);
          toast.success("Auto-renovación activada");
        })
        .catch(({ response }) => {
          console.error("Error: ", response);
          toast.error(`Error: ${response?.data?.error || "Error desconocido"}`);
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (membershipData) {
      setDetails(membershipData);
      setIsAutoRenewal(membershipData.is_auto_renewal);
    }
  }, [membershipData]);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: membershipLoading || !membershipData,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    const membershipId = membershipData?.id || details.id;
    if (membershipId) {
      dispatch(loadMembershipData(membershipId));
    }
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-8 w-80" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4 rounded-2xl border border-slate-200/70 bg-white/95 p-6">
      <div className="flex items-center justify-between">
        <SkeletonText className="h-4 w-56" />
        <SkeletonBlock className="h-8 w-24 rounded-md" />
      </div>
      <div className="h-px bg-slate-200" />
      <SkeletonText className="h-4 w-40" />
      <div className="h-px bg-slate-200" />
      <SkeletonText className="h-4 w-36" />
      <div className="h-px bg-slate-200" />
      <div className="space-y-1">
        <SkeletonText className="h-4 w-64" />
        <SkeletonText className="h-3 w-56" />
      </div>
      <div className="h-px bg-slate-200" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-1">
          <SkeletonText className="h-4 w-72" />
          <SkeletonText className="h-3 w-80" />
        </div>
        <SkeletonBlock className="h-8 w-28 rounded-md" />
      </div>
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <>
        <Title level={2}>Información de la membresía</Title>
        <Row>
          {details && (
            <Card style={{ paddingRight: "16px" }}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={18} md={20} lg={20} xl={20} xxl={20}>
                  <Text strong>Plan Contratado:</Text>{" "}
                  <Text>{details.plan}</Text>
                </Col>
                <Col xs={24} sm={6} md={4} lg={4} xl={4} xxl={4}>
                  <Button
                    ghost
                    type="primary"
                    onClick={() => navigate("/memberships/pricing")}
                  >
                    Cambiar
                  </Button>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col>
                  <Text strong>Precio:</Text> <Text>${details.price}</Text>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col>
                  <Text strong>Usuarios:</Text>{" "}
                  <Text>{details.user_members}</Text>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col span={24}>
                  <Text strong>Fecha de término de período:</Text>{" "}
                  <Text>{details.renewal_date ?? "N/A"}</Text>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Último día de uso del plan.</Text>
                </Col>
              </Row>
              <Divider />
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={18} md={20} lg={20} xl={20} xxl={20}>
                  <Row justify={"start"}>
                    <Col xs={24} sm={18} md={20} lg={20} xl={20} xxl={20}>
                      <Text strong>Cancelar plan al final del período:</Text>{" "}
                      <Text>{isAutoRenewal ? "Desactivado" : "Activado"}</Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Text type={"secondary"}>
                        Al finalizar el periodo, tu plan será cancelado
                        volviendo al plan <Text strong>Free.</Text>
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} sm={6} md={4} lg={4} xl={4} xxl={4}>
                  <Button
                    onClick={handleAutoRenewalAction}
                    loading={loading}
                    style={
                      isAutoRenewal
                        ? { borderColor: "#ff4d4f", color: "#ff4d4f" }
                        : { borderColor: "green", color: "green" }
                    }
                  >
                    {isAutoRenewal
                      ? loading
                        ? "Activando"
                        : "activar"
                      : loading
                        ? "Desactivando"
                        : "Desactivar"}
                  </Button>
                </Col>
              </Row>
            </Card>
          )}
        </Row>
      </>
    </PageLoadingContainer>
  );
};

export default MembershipConfiguration;
